import { useEffect, useRef, useState } from "react";
import type { SpeciesQuery } from "../lib/api";
import {
  localPlaceMatches,
  geoPlaceMatches,
  placeKeys,
  type PlaceItem,
} from "../lib/placeSearch";
import { useFishStore } from "../lib/storage";
import { useDistanceUnit, toKm, fromKm, unitLabel } from "../lib/units";
import { ChevIcon, PinIcon, SearchIcon } from "../ui";

// Radius presets — interpreted in the user's chosen distance unit.
const RADII = [5, 10, 25, 50];

export interface LocationSelection {
  name: string;
  radiusKm: number;
}

// Two-mode editing UI (per spot-layout-options.html picks D + G):
//   "tiles"  — no path chosen yet. Two equal-weight tiles ("Type a place",
//              "Use my location") + radius chip row below.
//   "typing" — chose the typing path. Full input + autocomplete dropdown
//              + "Find species" CTA + a Back link to return to tiles.
// Once a selection is set and !editing, the whole form collapses to a
// single Fraunces pill row: pin + name + radius + Change.
type EditMode = "tiles" | "typing";

export function LocationForm({
  onSearch,
  loading,
  selection,
  ctaLabel = "Find species",
}: {
  onSearch: (q: SpeciesQuery) => void;
  loading: boolean;
  selection: LocationSelection | null;
  // Submit-button text. Defaults to the Spot flow's "Find species"; the
  // Plan tab passes its own so the CTA reads right for planning.
  ctaLabel?: string;
}) {
  const [text, setText] = useState("");
  const [radius, setRadius] = useState(10);
  const unit = useDistanceUnit();
  const radiusKm = toKm(radius, unit);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [editing, setEditing] = useState(!selection);
  const [mode, setMode] = useState<EditMode>("tiles");

  const [results, setResults] = useState<PlaceItem[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [searching, setSearching] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipSearch = useRef(false);
  const visited = useFishStore((s) => s.visited_locations);

  // Collapse once a location is set; reset to tile picker when it's cleared.
  const selectionKey = selection
    ? `${selection.name}|${selection.radiusKm}`
    : "";
  useEffect(() => {
    const hasSel = !!selection;
    setEditing(!hasSel);
    if (!hasSel) setMode("tiles");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  // Focus the input after switching into typing mode so the keyboard opens
  // right away — anything else would waste the extra tap.
  useEffect(() => {
    if (mode === "typing") inputRef.current?.focus();
  }, [mode]);

  // Debounced autocomplete lookup. Known snorkel spots + your logged spots
  // show instantly (no network); general geocoded map locations are appended
  // when the debounced lookup returns. See the unified lib/placeSearch.
  useEffect(() => {
    if (skipSearch.current) {
      skipSearch.current = false;
      return;
    }
    const q = text.trim();
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      setSearching(false);
      return;
    }
    const local = localPlaceMatches(q, visited);
    setResults(local);
    setHighlight(-1);
    setOpen(true);
    setSearching(true);
    let cancelled = false;
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      geoPlaceMatches(q, placeKeys(local), ctl.signal)
        .then((geo) => {
          if (!cancelled) setResults([...local, ...geo]);
        })
        .catch(() => {
          /* network/abort — local matches still shown */
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      ctl.abort();
    };
    // Keyed on text (not `visited`) so typing drives the lookup; visited is
    // read fresh inside for the local matches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selectPlace = (p: PlaceItem) => {
    skipSearch.current = true;
    setText(p.name);
    setResults([]);
    setOpen(false);
    setHighlight(-1);
    setGeoError(null);
    onSearch({ lat: p.lat, lng: p.lng, name: p.name, radiusKm });
    setEditing(false);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (results.length > 0) {
      selectPlace(results[highlight >= 0 ? highlight : 0]);
      return;
    }
    if (!text.trim()) return;
    setOpen(false);
    onSearch({ location: text.trim(), radiusKm });
    setEditing(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      setResults([]);
      setHighlight(-1);
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    }
  };

  const useMyLocation = () => {
    setGeoError(null);
    if (!navigator.geolocation) {
      setGeoError("Geolocation isn't available in this browser.");
      return;
    }
    setOpen(false);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        onSearch({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          radiusKm,
        });
        setEditing(false);
      },
      (err) => {
        setLocating(false);
        setGeoError(
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied — re-enable it in your browser's location settings (iPhone: Settings › Safari › Location), or type a place name below."
            : "Couldn't get your location — try again or type a place name below.",
        );
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  // ---------- Collapsed pill (Option G) --------------------------------
  // Once a spot is set, the whole form becomes a single row: pin, place
  // name (Fraunces), inline radius meta, and a Change link on the right.
  // Frees the vertical space for the species list, which is the point of
  // the screen.
  if (selection && !editing) {
    return (
      <div className="card flex items-center gap-3 px-4 py-3">
        <PinIcon className="h-5 w-5 flex-none text-aqua-deep" />
        <p className="min-w-0 flex-1 truncate">
          <span className="font-display text-[17px] font-semibold text-ink">
            {selection.name}
          </span>
          <span className="ml-2 text-[13px] text-meta">
            · {fromKm(selection.radiusKm, unit)} {unitLabel(unit)} radius
          </span>
        </p>
        <button
          type="button"
          onClick={() => {
            setEditing(true);
            setMode("tiles");
          }}
          className="inline-flex min-h-[44px] flex-none items-center justify-center rounded-full px-3 text-[13px] font-bold text-aqua-deep ring-1 ring-inset ring-aqua-deep/20 hover:bg-aqua-deep/5"
        >
          Change
        </button>
      </div>
    );
  }

  // ---------- Radius chip row (shared across both edit modes) ----------
  const radiusRow = (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-meta">
        Radius
      </span>
      <div
        className="flex overflow-hidden rounded-full ring-1 ring-inset ring-ring"
        role="radiogroup"
        aria-label="Search radius"
      >
        {RADII.map((r) => (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={radius === r}
            onClick={() => setRadius(r)}
            className={`inline-flex min-h-[44px] items-center justify-center px-4 py-2.5 text-sm font-semibold transition ${
              radius === r
                ? "bg-aqua-deep text-paper"
                : "bg-paper text-meta hover:bg-ink/5"
            }`}
          >
            {r}
            {unitLabel(unit)}
          </button>
        ))}
      </div>
    </div>
  );

  // ---------- Two-tile picker (Option D) -------------------------------
  // Compressed horizontal layout — icon on the left, title stacked over a
  // one-line hint on the right. Reads as compact choices instead of tall
  // empty boxes; frees vertical space for the radius row and (later) the
  // species list to breathe.
  if (mode === "tiles") {
    return (
      <div className="card p-4 sm:p-5">
        <p className="mb-3 text-[10.5px] font-bold uppercase tracking-[0.14em] text-meta">
          Where are you snorkeling?
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setMode("typing")}
            className="flex items-center gap-3 rounded-xl bg-paper px-3.5 py-3 text-left shadow-[inset_0_0_0_1px_rgba(10,39,51,0.07)] transition hover:bg-aqua/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua-deep"
          >
            <SearchIcon className="h-5 w-5 flex-none text-aqua-deep" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-display text-[14.5px] font-semibold leading-tight text-ink">
                Type a place
              </span>
              <span className="block truncate text-[11px] leading-tight text-meta">
                Beach, bay or reef
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={useMyLocation}
            disabled={loading || locating}
            className="flex items-center gap-3 rounded-xl bg-paper px-3.5 py-3 text-left shadow-[inset_0_0_0_1px_rgba(10,39,51,0.07)] transition hover:bg-aqua/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua-deep disabled:opacity-60"
          >
            <PinIcon className="h-5 w-5 flex-none text-aqua-deep" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-display text-[14.5px] font-semibold leading-tight text-ink">
                {locating ? "Locating…" : "Use my location"}
              </span>
              <span className="block truncate text-[11px] leading-tight text-meta">
                Ask device GPS
              </span>
            </span>
          </button>
        </div>

        <div className="mt-4">{radiusRow}</div>

        {selection && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-[11px] font-bold uppercase tracking-[0.06em] text-aqua-deep hover:underline"
            >
              ← Keep {selection.name}
            </button>
          </div>
        )}

        {geoError && (
          <p className="mt-3 text-sm text-coral">{geoError}</p>
        )}
      </div>
    );
  }

  // ---------- Typing mode: input + autocomplete + CTA ------------------
  const showDropdown = open && text.trim().length >= 2;
  return (
    <form onSubmit={submit} className="card p-5 sm:p-6">
      <div className="mb-2 flex items-center justify-between gap-3">
        <label
          htmlFor="loc"
          className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-meta"
        >
          Where are you snorkeling?
        </label>
        <button
          type="button"
          onClick={() => setMode("tiles")}
          className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.06em] text-aqua-deep hover:underline"
        >
          <ChevIcon className="h-3 w-3 rotate-180" />
          Back
        </button>
      </div>
      <div ref={boxRef} className="relative">
        <input
          ref={inputRef}
          id="loc"
          className="field w-full"
          placeholder="Start typing a beach, bay or reef…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
          autoComplete="off"
          enterKeyHint="search"
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls="loc-listbox"
          // Point the screen reader at the arrow-key-highlighted option so
          // it announces each suggestion as the user moves through the list.
          // Without this the highlight is a silent visual-only change. See
          // location-form-combobox-missing-aria-activedescendant.
          aria-activedescendant={
            showDropdown && highlight >= 0 ? `loc-opt-${highlight}` : undefined
          }
        />
        {showDropdown && (
          <ul
            id="loc-listbox"
            role="listbox"
            className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-auto rounded-xl bg-paper py-1 shadow-card ring-1 ring-ring"
          >
            {results.map((p, i) => (
              // The option itself is the click target — no nested <button>.
              // The ARIA listbox pattern needs options non-focusable so
              // focus stays on the input and aria-activedescendant + arrow
              // keys drive selection; a real button would be its own Tab
              // stop and break that model. Keyboard users select via the
              // input's Enter handler. See
              // location-combobox-focusable-option-buttons.
              <li
                key={`${p.lat},${p.lng},${i}`}
                id={`loc-opt-${i}`}
                role="option"
                aria-selected={i === highlight}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => {
                  // preventDefault so the input doesn't blur (which would
                  // close the list) before the click registers.
                  e.preventDefault();
                  selectPlace(p);
                }}
                className={`flex min-h-[44px] w-full cursor-pointer flex-col items-start justify-center gap-0.5 px-3 py-2 text-left transition ${
                  i === highlight ? "bg-aqua/10" : "hover:bg-ink/5"
                }`}
              >
                <span className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                  <PinIcon className="h-4 w-4 text-aqua-deep" />
                  <span>{p.name}</span>
                </span>
                {p.detail && (
                  <span className="text-xs text-ink/50">{p.detail}</span>
                )}
              </li>
            ))}
            {searching && (
              <li className="px-3 py-2 text-sm text-ink/45">Searching…</li>
            )}
            {!searching && results.length === 0 && (
              <li className="px-3 py-2 text-sm text-ink/45">
                No matching places — press Enter to search anyway.
              </li>
            )}
          </ul>
        )}
      </div>

      <div className="mt-4">{radiusRow}</div>

      <button
        type="submit"
        className="btn-primary mt-4 w-full"
        disabled={loading || !text.trim()}
      >
        {loading ? "Searching…" : ctaLabel}
      </button>

      {geoError && <p className="mt-3 text-sm text-coral">{geoError}</p>}
    </form>
  );
}
